import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'

type RegionalHeaderScrollContextValue = {
  showRegionName: boolean
  setSentinelNode: (node: HTMLDivElement | null) => void
}

const RegionalHeaderScrollContext =
  createContext<RegionalHeaderScrollContextValue | null>(null)

export const RegionalHeaderScrollProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [sentinelNode, setSentinelNode] = useState<HTMLDivElement | null>(null)
  const [showRegionName, setShowRegionName] = useState(false)

  const setSentinelRef = useCallback((node: HTMLDivElement | null) => {
    setSentinelNode(node)
  }, [])

  useEffect(() => {
    if (!sentinelNode) {
      setShowRegionName(false)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowRegionName(!entry.isIntersecting)
      },
      { threshold: 0 },
    )

    observer.observe(sentinelNode)
    return () => observer.disconnect()
  }, [sentinelNode])

  return (
    <RegionalHeaderScrollContext.Provider
      value={{ showRegionName, setSentinelNode: setSentinelRef }}
    >
      {children}
    </RegionalHeaderScrollContext.Provider>
  )
}

export const RegionalHeaderScrollSentinel = () => {
  const context = useContext(RegionalHeaderScrollContext)

  if (!context) {
    return null
  }

  return (
    <div
      ref={context.setSentinelNode}
      aria-hidden
      style={{ width: '100%', height: 0, pointerEvents: 'none' }}
    />
  )
}

export const useRegionalHeaderScroll = () => {
  const context = useContext(RegionalHeaderScrollContext)

  if (!context) {
    throw new Error(
      'useRegionalHeaderScroll must be used within RegionalHeaderScrollProvider',
    )
  }

  return context
}
